import { boss } from '../entities/enemies';
import { mechEvent } from '../entities/mech-anim';
import { angDiff } from './fx';
import { enemies } from '../entities/enemies';
import { P } from '../world/player';

/* ============================================================
   手動鎖定

   自動瞄準永遠打最近的目標，於是「集中火力」這件事玩家做不到 ——
   而衝擊力系統（sim/impact.ts）要求集中火力才填得滿，兩者是同一件事的兩半。

   規則刻意簡單：
   · Tab（或滑鼠中鍵）在射程內的敵人之間循環切換
   · 鎖定的目標死亡或離開射程 → 自動解除，回到自動瞄準
   · 沒有手動鎖定時行為與原本完全相同

   排序用「與目前朝向的角度差」而不是距離：玩家按 Tab 的心理預期是
   「換旁邊那一隻」，不是「換遠一點那一隻」。
   ============================================================ */

export var LOCK_RANGE_MUL = 1.6;   // 鎖定射程比主武裝射程寬，讓玩家能預先鎖遠處目標

function inRange(e) {
  if (!e || !e.alive) return false;
  // Boss 一律可鎖，不受射程限制 —— 牠是全場唯一「你一定想盯著」的東西
  if (e.T.boss) return true;
  var r = P.s.atkRange * LOCK_RANGE_MUL;
  return Math.hypot(e.pos.x - P.pos.x, e.pos.z - P.pos.z) <= r;
}

/** 目前有效的鎖定目標，沒有就回傳 null。 */
export function lockedTarget() {
  return inRange(P.lockT) ? P.lockT : null;
}

export function clearLock() { P.lockT = null; P.lockOn = false; }

/** 射程內的目標，依 enemies 陣列順序（生成順序）—— 這是**穩定**的排序。

    早期版本依「與正面的角度差」排序，結果循環永遠壞掉：鎖定的目標就是砲塔
    正對著的那一隻，角度差 ≈ 0，所以它永遠排在第 0 位，「循環到最後一個」
    這個條件幾乎不會成立。依角度排序只適合「第一次要鎖誰」，不適合循環。 */
function candidates() {
  var list = [];
  for (var i = 0; i < enemies.length; i++) if (inRange(enemies[i])) list.push(enemies[i]);
  return list;
}

/** 第一次鎖定時挑最接近正面的那一隻 —— 這裡用角度差是對的。

    但**打得出踉蹌的目標優先**：雜魚（imp = 0）三發就死、不需要鎖定，
    而會踉蹌的那些正是你想集中火力的對象。若全場都是雜魚才退而求其次。 */
function nearestToFacing(list) {
  var pool = list.filter(function (e) { return e.T.imp > 0; });
  if (!pool.length) pool = list;
  var best = pool[0], bestA = Infinity;
  for (var i = 0; i < pool.length; i++) {
    var a = Math.abs(angDiff(Math.atan2(pool[i].pos.x - P.pos.x, pool[i].pos.z - P.pos.z), P.faceAim));
    if (a < bestA) { bestA = a; best = pool[i]; }
  }
  return best;
}

/** Tab（或滑鼠中鍵）：同一個鍵開關鎖定。

    未鎖定 → 鎖住最接近正面的那一隻，之後**黏著不放**（面向別處也不會換），
    這是「鎖定」與「自動瞄準」的差別所在。
    已鎖定 → 解除，回到自動瞄準。

    要換目標就「解除 → 轉向新目標 → 再鎖」。兩次按鍵，但規則只有一條，
    不需要記住循環順序 —— 早期的循環版依角度差排序，而鎖定的目標永遠正對砲塔、
    角度差恆為 0，循環實際上是壞的。 */
export function toggleLock() {
  if (P.lockOn) { clearLock(); return; }
  var list = candidates();
  if (!list.length) return;
  P.lockOn = true;
  P.lockT = nearestToFacing(list);
  mechEvent(P.rig, 'lock');
}

/** 長按 Tab：直接鎖 Boss。Boss 戰時場上還有一堆小兵，短按會鎖到牠們，
    而你想盯的幾乎一定是 Boss。沒有 Boss 時退化成一般的短按行為。 */
export function lockBoss() {
  if (boss && boss.alive) { P.lockOn = true; P.lockT = boss; mechEvent(P.rig, 'lock'); return; }
  toggleLock();
}

/** 每步呼叫。目標死掉或跑掉時**自動接續下一個**，不用玩家重按 ——
    小怪死得很快，每次都要重按 Tab 的話這個功能等於不能用。 */
export function updateLock() {
  if (!P.lockOn || inRange(P.lockT)) return;
  var list = candidates();
  P.lockT = list.length ? nearestToFacing(list) : null;
}
